import React, {useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {getCommissionDetail, getUserCommissionInfo} from './proxy.service';
import {defaultHeaderImg} from './proxy.variable';
import {
  ArrowRightIcon,
  ProxyCommissionRateModal,
  ProxyH5Header,
  goTo,
  toPriceStr,
  useInfinitePager,
} from './proxy-h5.shared';

type CommissionItem = {
  userId: number;
  userPhone: string;
  activeDay: string;
  commissionAmount: string;
  userAvatar?: string;
};

const TIER_BG = [
  'linear-gradient(90deg, #FCC -1.04%, #F26969 102.08%)',
  'linear-gradient(90deg, #CCEDFF -1.04%, #60BDE5 102.08%)',
  'linear-gradient(90deg, #FFF1CC -1.04%, #EEBF45 102.08%)',
  'linear-gradient(90deg, #CCD7FF -1.04%, #5982ED 102.08%)',
];

const TIER_COLOR = ['#800A0A', '#044F70', '#775005', '#052169'];
const emptyImage = require('@components/assets/imgs/empty.webp');

const SearchIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" className="h-4 w-4">
    <path
      d="M7.333 12.667A5.333 5.333 0 1 1 7.333 2a5.333 5.333 0 0 1 0 10.667Z"
      stroke="#C6CDD5"
      strokeWidth="1.5"
    />
    <path
      d="m14 14-2.9-2.9"
      stroke="#C6CDD5"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const ProxyCommissionDetail = (props: any) => {
  const {t} = useTranslation();
  const userId = props?.route?.params?.userId;
  const agentLevel = props?.route?.params?.agentLevel || 1;
  const [phone, setPhone] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [tier, setTier] = useState(1);
  const [totalCommission, setTotalCommission] = useState(0);
  const [openRate, setOpenRate] = useState(false);

  const titleText = t('proxy.commission-detail.title');
  const searchPlaceholder = t('proxy.commission-detail.phonePlaceholder');
  const totalText = t('proxy.commission-detail.totalCommission');
  const tierText = t('proxy.commission-detail.tierBtn');
  const amountText = t('proxy.commission-detail.commissionAmount');
  const noMoreText = t('proxy.commission-detail.noMore');
  const noDataText = t('proxy.commission-detail.noData');
  const rebateText = t('proxy.commission-detail.rebate');

  React.useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearchKeyword(phone.trim());
    }, 1000);

    return () => {
      window.clearTimeout(timer);
    };
  }, [phone]);

  React.useEffect(() => {
    if (!userId) {
      return;
    }
    getUserCommissionInfo(+userId).then(result =>
      setTotalCommission(+(result?.commissionAmount || 0)),
    );
  }, [userId]);

  const request = React.useCallback(
    (pageNo: number, pageSize: number) =>
      getCommissionDetail({
        pageNo,
        pageSize,
        phone: searchKeyword,
        grade: tier,
      }),
    [searchKeyword, tier],
  );

  const {items, loading, loadingMore, isEnd, sentinelRef} =
    useInfinitePager<CommissionItem>(request);

  const tierButtons = useMemo(() => [1, 2, 3, 4], []);

  return (
    <div className="proxy-h5-page flex min-h-full flex-col bg-[#f3f5f8]">
      <ProxyH5Header title={titleText} service={false} />
      <div className="flex-1 overflow-y-auto">
        <div className="bg-white">
          <div className="relative h-16 bg-white p-3">
            <div className="pointer-events-none absolute left-6 top-6">
              <SearchIcon />
            </div>
            <input
              type="number"
              value={phone}
              onChange={event => setPhone(event.target.value)}
              placeholder={searchPlaceholder}
              className="h-10 w-full rounded border border-[var(--proxy-line)] bg-white pl-9 pr-3 text-sm font-medium text-[var(--proxy-second)] outline-none placeholder:font-medium placeholder:text-[#B5BDC8]"
            />
          </div>
          <div className="border-t border-[var(--proxy-line)] bg-white">
            <div className="flex h-16 items-center justify-between px-4 pb-3 pt-3">
              <div>
                <div className="mb-1 text-sm font-medium text-[var(--proxy-accent)]">
                  {totalText}
                </div>
                <div className="font-[DIN] text-[1.25rem] font-bold leading-none text-[var(--proxy-second)]">
                  {toPriceStr(totalCommission, {fixed: 2, thousands: true})}
                </div>
              </div>
              <button
                type="button"
                className="flex h-5 w-16 items-center justify-center rounded-full bg-[var(--proxy-primary)] text-[10px] font-bold text-white"
                onClick={() => setOpenRate(true)}>
                <span>{rebateText}</span>
                <svg
                  width="10"
                  height="10"
                  viewBox="0 0 10 10"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="ml-0.5 h-[0.625rem] w-[0.625rem]">
                  <path
                    d="M7.253 4.824a.714.714 0 0 1 0 1.08l-2.48 2.148a.714.714 0 0 1-1.182-.54V3.216c0-.612.72-.94 1.182-.54z"
                    fill="#fff"
                  />
                </svg>
              </button>
            </div>
            <div className="flex items-center justify-between px-3 pb-3">
              {tierButtons.map(item => (
                <div
                  key={item}
                  className="flex h-9 flex-col items-center justify-start">
                  <button
                    type="button"
                    className="h-7 w-16 rounded-full text-xs font-medium tracking-[-0.3px]"
                    style={{
                      background: TIER_BG[item - 1],
                      color: TIER_COLOR[item - 1],
                    }}
                    onClick={() => setTier(item)}>
                    {`${tierText} ${item}`}
                  </button>
                  {item === tier && (
                    <div className="mt-1 h-1 w-5 bg-[var(--proxy-primary)]" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="px-3 pb-7 pt-3">
          {!loading && items.length === 0 && (
            <div className="flex min-h-[22rem] w-full flex-col items-center justify-center">
              <img src={emptyImage} alt="" className="h-40 w-40" />
              <div className="mt-5 text-sm text-[var(--proxy-accent)]">
                {noDataText}
              </div>
            </div>
          )}

          {items.map(item => (
            <div
              key={item.userId}
              className="mt-2 flex flex-col justify-between rounded-lg bg-white p-3 first:mt-0"
              onClick={() =>
                goTo('ProxyCommissionDetailUser', {userId: item.userId})
              }>
              <div className="mb-2 flex h-6 items-center justify-between">
                <div className="flex items-center">
                  <img
                    src={item.userAvatar || defaultHeaderImg}
                    alt=""
                    className="mr-1 h-6 w-6 rounded-full"
                  />
                  <div className="text-xs font-semibold tracking-[-0.3px] text-[var(--proxy-main)]">
                    {item.userPhone}
                  </div>
                </div>
                <div className="text-xs font-medium text-[var(--proxy-second)]">
                  {item.activeDay}
                </div>
              </div>
              <div className="flex h-9 items-center justify-between">
                <div className="text-sm font-medium tracking-[-0.3px] text-[var(--proxy-second)] opacity-80">
                  {amountText}
                </div>
                <div className="flex items-center font-[DIN] text-base font-bold tracking-[-0.3px] text-[var(--proxy-primary)]">
                  <span className="mr-1">
                    {`+ ${toPriceStr(+item.commissionAmount || 0)}`}
                  </span>
                  <ArrowRightIcon className="relative top-[0.0625rem] h-3 w-3 text-[#9FA5AC]" />
                </div>
              </div>
            </div>
          ))}

          {loading && (
            <div className="py-3 text-center text-sm text-[var(--proxy-text)]">
              Loading...
            </div>
          )}
          <div ref={sentinelRef} />
          {loadingMore && (
            <div className="py-3 text-center text-sm text-[var(--proxy-text)]">
              Loading...
            </div>
          )}
          {!loading && !loadingMore && isEnd && items.length > 0 && (
            <div className="flex items-center justify-center py-3 text-center text-xs font-medium tracking-[-0.3px] text-[var(--proxy-accent)]">
              {noMoreText}
            </div>
          )}
        </div>
      </div>
      <ProxyCommissionRateModal
        open={openRate}
        onClose={() => setOpenRate(false)}
        level={agentLevel}
        onInvite={() => setOpenRate(false)}
      />
    </div>
  );
};

export default ProxyCommissionDetail;
